import { useState, useEffect } from "react";
import { t, font, Avatar } from "@realtyflows/ui";
import { getFolderActivity } from "../api";

/**
 * FolderActivityFeed — V1 Section B
 *
 * Read-only audit trail for a folder. Shows every meaningful event in
 * reverse-chronological order: create, status change, upload, form add,
 * envelope sent, signed, etc.
 *
 * Activity rows are written by folderService.logActivity() throughout
 * the codebase — this component is a pure consumer.
 */

const TYPE_META = {
  folder_created:      { icon: "✨", color: "emerald" },
  folder_updated:      { icon: "✎",  color: "muted" },
  status_changed:      { icon: "⇄",  color: "sky" },
  upload_added:        { icon: "↥",  color: "sky" },
  upload_edited:       { icon: "✎",  color: "muted" },
  upload_downloaded:   { icon: "↧",  color: "muted" },
  upload_removed:      { icon: "×",  color: "muted" },
  template_applied:    { icon: "★",  color: "amber" },
  envelope_sent:       { icon: "✉",  color: "amber" },
  envelope_signed:     { icon: "✓",  color: "emerald" },
  envelope_voided:     { icon: "⊘",  color: "coral" },
  compliance_changed:  { icon: "☑",  color: "sky" },
  permission_granted:  { icon: "+",  color: "emerald" },
  permission_revoked:  { icon: "−",  color: "muted" },
};

function colorOf(name) {
  return ({ emerald: t.emerald, amber: t.amber, sky: t.sky, coral: t.coral, muted: t.textMuted })[name] || t.textMuted;
}

function fmtRelative(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return "just now";
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  const days = Math.floor(sec / 86400);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function FolderActivityFeed({ folderId, limit = 30 }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!folderId) return;
    getFolderActivity(folderId, { limit })
      .then(setItems)
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [folderId, limit]);

  if (loading) {
    return <div style={{ fontSize: 12, color: t.textMuted, padding: 10, fontFamily: font.body }}>Loading activity...</div>;
  }
  if (items.length === 0) {
    return (
      <div style={{ fontSize: 12, color: t.textMuted, fontStyle: "italic", padding: 10, fontFamily: font.body }}>
        No activity yet. Events will appear here as you work in this folder.
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
      {items.map((row, idx) => {
        const meta = TYPE_META[row.type] || { icon: "•", color: "muted" };
        const color = colorOf(meta.color);
        const isLast = idx === items.length - 1;

        return (
          <div key={row.id} style={{ display: "flex", gap: 10, position: "relative", paddingBottom: isLast ? 0 : 14 }}>
            {/* Vertical timeline line */}
            {!isLast && (
              <div style={{
                position: "absolute", left: 11, top: 26, bottom: 0,
                width: 1, background: t.border,
              }} />
            )}

            {/* Icon */}
            <div style={{
              width: 24, height: 24, borderRadius: 99,
              background: color + "20",
              border: `1px solid ${color}40`,
              color, flexShrink: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 12, fontWeight: 700,
              zIndex: 1,
            }}>
              {meta.icon}
            </div>

            {/* Content */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, color: t.textPrimary, fontFamily: font.body, lineHeight: 1.4 }}>
                {row.summary}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 3, fontSize: 10, color: t.textMuted, fontFamily: font.body }}>
                {row.actor_name && (
                  <>
                    <Avatar src={row.actor_photo} name={row.actor_name} size={14} />
                    <span>{row.actor_name}</span>
                    <span>·</span>
                  </>
                )}
                <span>{fmtRelative(row.created_at)}</span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
